import { ActivityDetailSheet } from '@/components/passport/ActivityDetailSheet';
import { ActivityRow } from '@/components/passport/ActivityRow';
import { ChecklistActivityRow } from '@/components/passport/ChecklistActivityRow';
import { ParticipantCard } from '@/components/passport/ParticipantCard';
import { PassportCover } from '@/components/passport/PassportCover';
import { PassportPage } from '@/components/passport/PassportPage';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/passport/PassportTabs';
import { PlatformBrand } from '@/components/passport/PlatformBrand';
import { ProgressRing } from '@/components/passport/ProgressRing';
import { CertificateBanner } from '@/components/passport/RaffleBanner';
import { SponsorRow } from '@/components/passport/SponsorRow';
import { StampBadge } from '@/components/passport/StampBadge';
import { SupporterRow } from '@/components/passport/SupporterRow';
import { WhatsAppContactBanner } from '@/components/passport/WhatsAppContactBanner';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { apiFetch } from '@/lib/api';
import { usePassportStore } from '@/stores/passport-store';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import type { PassportActivityDto, PassportDto as PassportDtoType } from '@tunodo/ticketing-shared';
import {
  PassportDto,
  PassportSponsorListDto,
  isCertifiableActivity,
} from '@tunodo/ticketing-shared';
import { ArrowLeft, IdCard, QrCode } from 'lucide-react';
import { useState } from 'react';

export const Route = createFileRoute('/passport/events_/$eventId')({
  beforeLoad: () => {
    const { user } = usePassportStore.getState();
    if (!user) {
      throw redirect({ to: '/passport' });
    }
  },
  component: PassportEventDetailPage,
});

function PassportEventDetailPage() {
  const user = usePassportStore((s) => s.user)!;
  const navigate = useNavigate();
  const { eventId } = Route.useParams();
  const [selectedActivity, setSelectedActivity] = useState<PassportActivityDto | null>(null);

  const { data, isLoading } = useQuery<PassportDtoType>({
    queryKey: ['passport', user.userId, 'events', eventId],
    queryFn: () =>
      apiFetch(`/api/passport/${user.userId}/events/${eventId}`, {
        schema: PassportDto,
      }),
  });

  const { data: sponsors = [] } = useQuery({
    queryKey: ['passport', 'sponsors', eventId],
    queryFn: () =>
      apiFetch(`/api/passport/events/${eventId}/sponsors`, {
        schema: PassportSponsorListDto,
      }),
  });

  const sections = data?.sections.slice().sort((a, b) => a.order - b.order) ?? [];
  const allActivities = sections.flatMap((s) => s.activities);
  const certifiableActivities = allActivities.filter((a) => isCertifiableActivity(a.type));
  const unlocked = certifiableActivities.filter((a) => a.unlockedAt !== null).length;
  const total = certifiableActivities.length;
  const isUnlocked = total > 0 && unlocked === total;

  // El header oscuro de navegación (back/scan) persiste pase lo que pase
  return (
    <div className="min-h-screen bg-passport-cover">
      <header className="sticky top-0 z-30 grid grid-cols-[auto_1fr_auto] sm:grid-cols-3 items-center gap-2 bg-passport-cover px-4 py-3 text-white shadow-md">
        <Button
          variant="ghost"
          size="sm"
          className="h-auto justify-self-start p-0 text-white hover:bg-white/20 hover:text-white"
          onClick={() => navigate({ to: '/passport/events' })}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <img
          src="/cimoips-white.png"
          alt="CIMO IPS"
          className="h-7 justify-self-start sm:justify-self-center"
        />
        <PlatformBrand variant="dark" className="justify-self-end" />
      </header>

      <div className="mx-auto flex max-w-3xl flex-col gap-5 p-4 pb-12">
        <PassportCover />

        <div
          className="rounded-2xl bg-passport-page p-4 shadow-xl sm:p-6"
          style={{ boxShadow: '0 24px 48px -16px rgba(0,0,0,0.45)' }}
        >
          {/* Header con progreso + CTA */}
          <div
            className="flex flex-col items-center gap-4 border-b pb-5 sm:flex-row sm:items-center sm:justify-between"
            style={{ borderColor: 'var(--passport-border)' }}
          >
            <ProgressRing unlocked={unlocked} total={total} />
            <div className="flex flex-col gap-2 sm:flex-row">
              {data?.attendeeProfile?.qrToken && (
                <Button
                  variant="outline"
                  className="gap-2 sm:w-auto"
                  onClick={() =>
                    window.open(`/badge?token=${data.attendeeProfile?.qrToken}`, '_blank')
                  }
                >
                  <IdCard className="h-4 w-4" />
                  Ver mi credencial
                </Button>
              )}
              <Button
                className="gap-2 text-white sm:w-auto"
                style={{ backgroundColor: 'var(--passport-ink-green)' }}
                onClick={() => navigate({ to: '/passport/scan/$eventId', params: { eventId } })}
              >
                <QrCode className="h-4 w-4" />
                Escanear sello
              </Button>
            </div>
          </div>

          {/* Cuerpo: tabs por sección */}
          <div className="mt-5">
            {isLoading ? (
              <div className="flex flex-col gap-3">
                <Skeleton className="h-10 w-full rounded-lg" />
                <Skeleton className="h-48 w-full rounded-xl" />
              </div>
            ) : sections.length === 0 ? (
              <div
                className="rounded-xl border bg-white/40 p-8 text-center"
                style={{ borderColor: 'var(--passport-border)' }}
              >
                <p
                  className="font-passport text-base italic"
                  style={{ color: 'var(--passport-ink-gray)' }}
                >
                  Aún no hay actividades publicadas para este evento.
                </p>
              </div>
            ) : (
              <Tabs defaultValue={sections[0]?.id ?? ''}>
                <TabsList className="border-b">
                  {sections.map((section) => (
                    <TabsTrigger key={section.id} value={section.id}>
                      {section.title}
                    </TabsTrigger>
                  ))}
                  <TabsTrigger value="__apoyan__">Apoyan</TabsTrigger>
                </TabsList>

                {sections.map((section, idx) => {
                  const activities = section.activities.slice().sort((a, b) => a.order - b.order);
                  const talks = activities.filter((a) => a.type === 'TALK');
                  const sponsors = activities.filter((a) => a.type === 'SPONSOR');
                  const checklist = activities.filter((a) => a.type === 'SECONDARY');
                  return (
                    <TabsContent key={section.id} value={section.id} className="mt-4">
                      <PassportPage pageNumber={idx + 1}>
                        <h2
                          className="font-passport text-2xl font-bold"
                          style={{ color: 'var(--passport-cover)' }}
                        >
                          {section.title}
                        </h2>
                        {section.description && (
                          <p
                            className="mt-1 font-passport text-sm italic"
                            style={{ color: 'var(--passport-ink-gray)' }}
                          >
                            {section.description}
                          </p>
                        )}

                        {activities.length === 0 ? (
                          <p
                            className="mt-6 text-center font-passport text-sm italic"
                            style={{ color: 'var(--passport-ink-gray)' }}
                          >
                            Sin actividades en esta sección.
                          </p>
                        ) : (
                          <>
                            {talks.length > 0 && (
                              <>
                                <div className="mt-5 grid grid-cols-3 gap-3 sm:grid-cols-5">
                                  {talks.map((a) => (
                                    <StampBadge
                                      key={a.id}
                                      icon={a.icon}
                                      label={a.title}
                                      sealed={a.unlockedAt !== null}
                                    />
                                  ))}
                                </div>
                                <div className="mt-6">
                                  {talks.map((a) => (
                                    <div key={a.id} className="relative">
                                      {a.unlockedAt === null &&
                                        talks.find((t) => t.unlockedAt === null)?.id === a.id && (
                                          <span className="pointer-events-none absolute -top-1 -right-1 z-10 flex h-3 w-3">
                                            <span className="animate-ping motion-reduce:animate-none absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                                            <span className="relative inline-flex h-3 w-3 rounded-full bg-amber-500" />
                                          </span>
                                        )}
                                      <ActivityRow
                                        activity={a}
                                        onClick={() => setSelectedActivity(a)}
                                      />
                                    </div>
                                  ))}
                                </div>
                              </>
                            )}

                            {sponsors.length > 0 && (
                              <div className="mt-5 flex flex-col gap-2">
                                <p
                                  className="px-1 text-xs font-semibold uppercase tracking-wider opacity-50"
                                  style={{ color: 'var(--passport-ink-gray)' }}
                                >
                                  Patrocinadores
                                </p>
                                {sponsors.map((a) => (
                                  <div key={a.id} className="relative">
                                    {a.unlockedAt === null &&
                                      talks.every((t) => t.unlockedAt !== null) &&
                                      sponsors.find((s) => s.unlockedAt === null)?.id === a.id && (
                                        <span className="pointer-events-none absolute -top-1 -right-1 z-10 flex h-3 w-3">
                                          <span className="animate-ping motion-reduce:animate-none absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                                          <span className="relative inline-flex h-3 w-3 rounded-full bg-amber-500" />
                                        </span>
                                      )}
                                    <SponsorRow
                                      activity={a}
                                      onClick={() => setSelectedActivity(a)}
                                    />
                                  </div>
                                ))}
                              </div>
                            )}

                            {checklist.length > 0 && (
                              <div
                                className="mt-5 rounded-xl border p-3"
                                style={{ borderColor: 'var(--passport-border)' }}
                              >
                                <p
                                  className="mb-2 px-1 text-xs font-semibold uppercase tracking-wider opacity-50"
                                  style={{ color: 'var(--passport-ink-gray)' }}
                                >
                                  Actividades
                                </p>
                                {checklist.map((a) => (
                                  <ChecklistActivityRow
                                    key={a.id}
                                    activity={a}
                                    userId={user.userId}
                                    eventId={eventId}
                                  />
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </PassportPage>
                    </TabsContent>
                  );
                })}
                <TabsContent value="__apoyan__" className="mt-4">
                  <PassportPage pageNumber={sections.length + 1}>
                    <h2
                      className="font-passport text-2xl font-bold"
                      style={{ color: 'var(--passport-cover)' }}
                    >
                      Apoyan
                    </h2>
                    <div className="mt-5 flex flex-col gap-2">
                      {sponsors.map((s) => (
                        <SupporterRow
                          key={s.id}
                          name={s.name}
                          description={s.description}
                          logoUrl={s.logoUrl ?? undefined}
                          fit={s.fit}
                        />
                      ))}
                    </div>
                  </PassportPage>
                </TabsContent>
              </Tabs>
            )}
          </div>

          {/* Datos del participante */}
          {data && (
            <div className="mt-6 border-t pt-6" style={{ borderColor: 'var(--passport-border)' }}>
              <ParticipantCard email={data.email} profile={data.attendeeProfile} />
            </div>
          )}
        </div>

        <CertificateBanner
          totalSeals={unlocked}
          totalActivities={total}
          isUnlocked={isUnlocked}
          onDownload={() =>
            window.open(`/api/passport/${user.userId}/events/${eventId}/certificate`, '_blank')
          }
        />

        <WhatsAppContactBanner phone="573142108221" message="Buen día" />
      </div>
      <ActivityDetailSheet
        activity={selectedActivity}
        open={!!selectedActivity}
        onClose={() => setSelectedActivity(null)}
      />
    </div>
  );
}
